// Polyfill Buffer for amazon-cognito-identity-js BEFORE any other import —
// the SDK accesses Buffer at module load via `Buffer.from(...)` calls in
// AuthenticationHelper. `global` is handled by vite.config.ts.
import { Buffer } from "buffer";
declare global {
  // eslint-disable-next-line no-var
  var Buffer: typeof import("buffer").Buffer;
}
if (!globalThis.Buffer) globalThis.Buffer = Buffer;

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { CrashBoundary, DateFormatProvider } from "@heraion/ui";
import { App } from "./App";
import "./i18n";
import { browserLocale, dateFormatStorage } from "./i18n/dateFormatStorage";
import "./index.css";
import { startReconnectSignal } from "./net";

// Note: the brand font default for every Text/TextInput is applied by the
// react-native shim (src/rnShim.ts, wired in vite.config alias), because on web
// the react-native namespace is frozen and can't be patched at runtime.

startReconnectSignal();

const root = document.getElementById("root");
if (!root) throw new Error("#root not found");

// Read once, outside React: the fallback resolves its own theme with the pure
// resolveTheme() because the ThemeProvider may be what crashed.
const prefersDark =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

// RW-19c — <SafeAreaProvider> sits where apps/mobile/App.tsx:100 puts it,
// above the theme provider. It is not decoration: react-native-safe-area-
// context v5 THROWS "No safe area value available" from useSafeAreaInsets
// when no provider is above it, and the lifted Logger reaches for it in six
// files (LoggerScreen's own <SafeAreaView> among them). Mobile never noticed
// the difference because on native <SafeAreaView> is a native view that needs
// no context — on web it resolves through the hook, so opening the Logger
// threw straight into the crash boundary. RW-18 lifted the screen and left
// the ambient providers its tree assumes behind on mobile.
createRoot(root).render(
  <StrictMode>
    <CrashBoundary
      prefersDark={prefersDark}
      onReload={() => window.location.reload()}
      logTag="web"
    >
      <SafeAreaProvider>
        <DateFormatProvider storage={dateFormatStorage} deviceLocale={browserLocale()}>
          <App />
        </DateFormatProvider>
      </SafeAreaProvider>
    </CrashBoundary>
  </StrictMode>,
);
